/**
 * PostCard Component
 *
 * Displays a post in a card format with status badge.
 * Clickable to open review modal.
 * Enhanced with visual hierarchy using card styles.
 */

import PropTypes from "prop-types";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import CardActionArea from "@mui/material/CardActionArea";
import Icon from "@mui/material/Icon";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDBadge from "components/MDBadge";

// Card styles
import { cardStyles, getStatusConfig } from "./cardStyles";

// Helper function to format date with relative time
const formatDate = (dateString) => {
  if (!dateString) return "Unknown date";
  const date = new Date(dateString);
  const now = new Date();
  const diffTime = now - date;
  const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

  if (diffDays === 0) return "Today";
  if (diffDays === 1) return "Yesterday";
  if (diffDays < 7) return `${diffDays} days ago`;

  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

// Helper function to get status badge color
const getStatusColor = (status) => {
  switch (status) {
    case "Posted":
      return "success";
    case "New":
      return "info";
    case "Good to go":
      return "warning";
    case "Rejected":
      return "error";
    default:
      return "secondary";
  }
};

// Get card style based on post status
const getCardStyleForStatus = (status) => {
  switch (status) {
    case "Pending Approval":
      return cardStyles.draft;
    case "Posted":
      return cardStyles.posted;
    case "Rejected":
      return cardStyles.rejected;
    default:
      return cardStyles.default;
  }
};

function PostCard({ post, onClick }) {
  const statusConfig = getStatusConfig(post.status);
  const cardStyle = getCardStyleForStatus(post.status);

  return (
    <Card
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        borderRadius: "16px",
        overflow: "hidden",
        cursor: "pointer",
        position: "relative",
        ...cardStyle,
        "&:hover": {
          transform: "translateY(-6px)",
          ...cardStyle["&:hover"],
        },
        "&:active": {
          transform: "translateY(-2px)",
        },
        "@media (hover: none)": {
          "&:hover": {
            transform: "none",
          },
          "&:active": {
            transform: "scale(0.98)",
          },
        },
      }}
    >
      <CardActionArea
        onClick={() => onClick(post)}
        sx={{
          height: "100%",
          display: "flex",
          flexDirection: "column",
          alignItems: "stretch",
          touchAction: "manipulation",
        }}
      >
        {/* Banner Image */}
        {post.banner_image && (
          <MDBox
            component="img"
            src={post.banner_image}
            alt={post.title}
            sx={{
              width: "100%",
              height: { xs: 160, sm: 180, md: 200 },
              objectFit: "cover",
              transition: "transform 0.3s ease",
              ".MuiCard-root:hover &": {
                transform: "scale(1.05)",
              },
            }}
          />
        )}

        <CardContent sx={{ flexGrow: 1, width: "100%", p: { xs: 2, sm: 2.5, md: 3 } }}>
          {/* Status Badge and Date */}
          <MDBox
            mb={2}
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            flexWrap="wrap"
            gap={1}
          >
            {/* Enhanced Status Badge */}
            <MDBox
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 0.5,
                px: 1.5,
                py: 0.5,
                borderRadius: "20px",
                backgroundColor: statusConfig.bgColor,
                color: statusConfig.textColor,
              }}
            >
              <Icon sx={{ fontSize: "0.9rem !important" }}>{statusConfig.icon}</Icon>
              <MDTypography
                variant="caption"
                sx={{
                  fontWeight: 600,
                  fontSize: "0.7rem",
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                  color: "inherit",
                }}
              >
                {statusConfig.label}
              </MDTypography>
            </MDBox>
            <MDTypography variant="caption" color="text" fontWeight="medium" sx={{ opacity: 0.8 }}>
              {formatDate(post.created_at)}
            </MDTypography>
          </MDBox>

          {/* Title */}
          <MDBox mb={1.5}>
            <MDTypography
              variant="h5"
              fontWeight="bold"
              gutterBottom
              sx={{
                fontSize: { xs: "1.05rem", sm: "1.15rem", md: "1.25rem" },
                lineHeight: 1.35,
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
                textOverflow: "ellipsis",
                color: "#1a1a2e",
              }}
            >
              {post.title}
            </MDTypography>
          </MDBox>

          {/* Source Channel Tag */}
          {post.sourceChannel && (
            <MDBox mb={2}>
              <MDBox
                component="span"
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 0.5,
                  px: 1,
                  py: 0.25,
                  borderRadius: "6px",
                  backgroundColor: "rgba(26, 115, 232, 0.08)",
                  fontSize: "0.7rem",
                  fontWeight: 500,
                  color: "#1A73E8",
                }}
              >
                <Icon sx={{ fontSize: "0.85rem !important" }}>source</Icon>
                {post.sourceChannel}
              </MDBox>
            </MDBox>
          )}

          {/* Abstract */}
          <MDBox mb={1}>
            <MDTypography
              variant="body2"
              color="text"
              sx={{
                display: "-webkit-box",
                WebkitLineClamp: 3,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
                textOverflow: "ellipsis",
                lineHeight: 1.65,
                fontSize: { xs: "0.85rem", sm: "0.875rem" },
                color: "#4a4a68",
              }}
            >
              {post.abstract}
            </MDTypography>
          </MDBox>

          {/* Source Headline (if available) */}
          {post.sourceHeadline && (
            <MDBox mt={2} pt={2} borderTop="1px solid" borderColor="grey.300">
              <MDTypography variant="caption" color="text" fontStyle="italic">
                Source: {post.sourceHeadline}
              </MDTypography>
            </MDBox>
          )}
        </CardContent>
      </CardActionArea>
    </Card>
  );
}

PostCard.propTypes = {
  post: PropTypes.shape({
    id: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    abstract: PropTypes.string,
    status: PropTypes.string.isRequired,
    banner_image: PropTypes.string,
    created_at: PropTypes.string,
    sourceHeadline: PropTypes.string,
    sourceChannel: PropTypes.string,
  }).isRequired,
  onClick: PropTypes.func.isRequired,
};

export default PostCard;
